import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";

// Redux
import { connect } from "react-redux";

// Components
import IkTable from "@Components/IkTable";
import HistoricChatModal from "../../../ReportClosed/Modals/HistoricChatModal";

// Utils
import dataHandler from "@Utils/dataHandler";
import { timeBetweenDates } from "@Components/IkTable/Utils/functions";

// Styles
import styles from "./styles.module.css";

const FirstResponse = ({ dispatch, userName, companyName, onOpenFilterModal, appliedFilters = {} }) => {
	const [filteredItems, setFilteredItems] = useState([]);
	const [filteredCount, setFilteredCount] = useState(0);
	const [filteredPrintData, setFilteredPrintData] = useState([]);
	const [selectedChatData, setSelectedChatData] = useState(null);
	const [isModalOpen, setIsModalOpen] = useState(false);

	const defaultParams = { page: 1, orderDir: "desc", rows: 20 };
	const [tableParams, setTableParams] = useState(defaultParams);
	const tableFields = "channel:platform.name,channel.label,group.name,user.fullName,createdDate,contact.phone,contact.name,tags.name";

	useEffect(() => {
		(async () => {
			try {
				setTableParams(defaultParams);
				const response = await dataHandler({
					dispatch,
					method: "GET",
					url: "/reports/historic/detailed/firstResponse",
					params: { ...defaultParams, ...appliedFilters, fields: tableFields },
				});

				setFilteredItems(response.items || []);
				setFilteredCount(response.count || 0);
			} catch (error) {
				setFilteredItems([]);
				setFilteredCount(0);
			}
		})();
	}, [appliedFilters]);

	const handlerData = async (params = defaultParams, printable) => {
		if (!printable) {
			setTableParams(params);
		}
		const data = await dataHandler({
			method: "GET",
			dispatch,
			url: "/reports/historic/detailed/firstResponse",
			params: { ...params, ...appliedFilters, fields: tableFields },
		});
		if (printable) {
			setFilteredPrintData(data.items);
		} else {
			setFilteredItems(data.items);
			setFilteredCount(data.count);
		}
	};

	const handleRefreshData = () => {
		setTableParams(defaultParams);
		handlerData({ ...defaultParams, ...appliedFilters });
	};

	const renderTopButtons = () => {
		const hasFilters = Object.keys(appliedFilters || {}).length > 0;
		const filterText = hasFilters ? "Filtrando" : "";

		const buttons = [
			{
				icon: "filter",
				className: hasFilters ? styles.filterButtonActive : styles.filterButton,
				onClick: onOpenFilterModal,
				content: filterText,
				label: filterText,
				showLabel: true,
				componentType: "button",
			},
			{
				icon: "refresh",
				className: styles.refreshButton,
				onClick: handleRefreshData,
			},
		];

		const dateRange = appliedFilters?.createdDate || appliedFilters?.closeDate;
		if (dateRange) {
			const formatDate = (d) => {
				if (!d) return "";
				const parts = d.split("-");
				if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
				return d;
			};
			const [start, end] = dateRange.split(",");
			let value = formatDate(start);
			const today = new Date();
			const currentEnd =
				end || `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
			if (currentEnd && start !== currentEnd) value += ` al ${formatDate(currentEnd)}`;

			buttons.push({
				componentType: "Label",
				title: "Fecha:",
				value,
			});
		}

		return buttons;
	};

	const handleChatClick = (item) => {
		setSelectedChatData(item);
		setIsModalOpen(true);
	};

	const handleChatKeyDown = (e, item) => {
		if (e.key === "Enter" || e.key === " ") {
			e.preventDefault();
			setSelectedChatData(item);
			setIsModalOpen(true);
		}
	};
	return (
		<div className={styles.container}>
			<div className={styles.containerTableGroup}>
				<div className={styles.tableGroupList}>
					<IkTable
						data={filteredItems}
						columns={[
							{
								label: "Chat ID",
								key: "id",
								format: "string",
								headerPosition: "center",
								dataPosition: "center",
								width: 1,
								render: (item) => {
									const id = item.chatId || item.id;
									return (
										<span
											className={styles.clickableChatId}
											role="button"
											tabIndex={0}
											onClick={() => {
												return handleChatClick(item);
											}}
											onKeyDown={(e) => {
												return handleChatKeyDown(e, item);
											}}>
											{id}
										</span>
									);
								},
							},
							{
								label: "Plataforma",
								key: "channel.platform.name",
								format: "string",
								dataPosition: "center",
								width: 0.5,
							},
							{ label: "Cuenta", key: "channel.label", format: "string", dataPosition: "center", width: 0.5 },
							{ label: "Grupos", key: "group.name", format: "string", dataPosition: "left", width: 1 },
							{ label: "Usuario", key: "user.fullName", format: "string", dataPosition: "left", width: 1 },
							{ label: "Contacto", key: "contact.name", format: "string", headerPosition: "center", dataPosition: "left", width: 1 },
							{ label: "Teléfono", key: "contact.phone", format: "phone", headerPosition: "center", dataPosition: "center", width: 1 },
							{
								label: "F. Entrada",
								key: "createdDate",
								format: "dateTime",
								dataPosition: "center",
								width: 1,
							},
							{
								label: "1ra Respuesta",
								key: "firstResponseDate",
								format: "dateTime",
								dataPosition: "center",
								width: 1,
							},
							{
								label: "Tiempo Espera",
								key: "createdDate",
								format: "string",
								dataPosition: "left",
								canSort: false,
								canFilter: false,
								width: 1,
								render: (data) => {
									const end = data.firstResponseDate ? data.firstResponseDate : data.closeDate;
									return timeBetweenDates(data.createdDate, end);
								},
							},
							{
								label: "Etiquetas",
								key: "tags,name",
								format: "arrayTruncate",
								width: 1,
							},
							{
								label: "F. Cierre",
								key: "closeDate",
								format: "dateTime",
								headerPosition: "left",
								dataPosition: "left",
								width: 16,
							},
						]}
						totalRows={filteredCount}
						filteredPrintData={filteredPrintData}
						defaultParams={defaultParams}
						cafParams={{ ...tableParams, ...appliedFilters }}
						dataHandler={handlerData}
						print={{
							printHeader: {
								company: companyName,
								title: "Primera Respuesta",
								user: userName,
							},
						}}
						searcher={{
							searchable: true,
							searchPosition: "flex-start",
							searchDisabled: false,
							filterButton: false,
							width: "40rem",
						}}
						topButtons={renderTopButtons()}
					/>
				</div>
			</div>
			{isModalOpen && (
				<HistoricChatModal
					open={isModalOpen}
					onClose={() => {
						setIsModalOpen(false);
						setSelectedChatData(null);
					}}
					chatData={selectedChatData}
					companyName={companyName}
					userName={userName}
					variant="firstResponse"
				/>
			)}
		</div>
	);
};

function mapStateToProps({ user, company }) {
	return {
		userName: user.name,
		companyName: company.name,
	};
}

FirstResponse.propTypes = {
	companyName: PropTypes.string,
	userName: PropTypes.string,
	dispatch: PropTypes.func,
	onOpenFilterModal: PropTypes.func,
	appliedFilters: PropTypes.object,
};

export default connect(mapStateToProps)(FirstResponse);
